import { useEffect, useRef, useState } from 'react';

import { pt } from '../copy';
import { shareJourney } from '../lib/api';

type ShareButtonProps = {
  location: string;
  prompt: string;
  frames: { year: number; b64: string }[];
  onShared: (id: string, url: string) => void;
  onError: (msg: string) => void;
};

type ShareState = 'idle' | 'loading' | 'success' | 'error';

export default function ShareButton({
  location,
  prompt,
  frames,
  onShared,
  onError,
}: ShareButtonProps) {
  const [status, setStatus] = useState<ShareState>('idle');
  const resetTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current !== null) {
        window.clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  const handleClick = async () => {
    if (status === 'loading' || frames.length < 2) {
      return;
    }

    setStatus('loading');

    try {
      const result = await shareJourney({
        location,
        prompt,
        frames,
      });
      const publicUrl = `${window.location.origin}/j/${result.id}`;
      await navigator.clipboard.writeText(publicUrl);
      setStatus('success');
      scheduleReset();
      onShared(result.id, publicUrl);
    } catch {
      setStatus('error');
      scheduleReset();
      onError(pt.shareError);
    }
  };

  const disabled = frames.length < 2 || status === 'loading';
  const label =
    status === 'loading'
      ? pt.generateCtaLoading
      : status === 'success'
        ? pt.shareSuccess
        : status === 'error'
          ? pt.shareError
          : pt.shareCta;

  return (
    <button
      className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/10 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/15 disabled:cursor-not-allowed disabled:border-white/5 disabled:bg-white/5 disabled:text-white/35"
      disabled={disabled}
      type="button"
      onClick={() => {
        void handleClick();
      }}
    >
      {status === 'loading' ? (
        <span
          aria-hidden="true"
          className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"
        />
      ) : null}
      <span>{label}</span>
    </button>
  );

  function scheduleReset() {
    if (resetTimerRef.current !== null) {
      window.clearTimeout(resetTimerRef.current);
    }
    resetTimerRef.current = window.setTimeout(() => {
      setStatus('idle');
      resetTimerRef.current = null;
    }, 1800);
  }
}
